import { DateTime } from '@ksyos/date-time';
import { Alert } from '@ksyos/design-system';
import { intersection, keys, pull } from 'lodash';
import { connect } from 'react-redux';
import { PatientDetailsChangeSource } from '@common/enums/patientDetailsChangeSource';
import type { Gender } from '../../../../../common/enums/gender';
import type { Profession } from '../../../../../common/enums/profession';
import type { IReduxState } from '../../../../configureStore';
import type { ValidationErrors } from '../../../../utils/validation';
import { RadioButtonGroupSelect } from '../../../../../common/enums/radioButtonGroupSelect';
import { getDisplayName } from '../../../../../common/utils/getDisplayName';
import { patientSsoUpdateSchema } from '../../../../../common/validation/patient';
import { AbortableFetch, fetchJson } from '../../../../store/fetch';
import { doesTranslateKeyExist, translateKey } from '../../../../translate';
import { emptyValidationErrors, validate } from '../../../../utils/validation';
import {
    ButtonContainer,
    Form,
    FormDivider,
    FormParagraph,
    InputRadio,
    ListTable,
    Loading,
    SaveSubmit,
    TableBody,
    TableData,
    TableHead,
    TableRow,
} from '../../../elements/common';
import { Component } from '../../../elements/Component';
import { DisplayAge } from '../../../elements/DisplayAge';
import { FormLabel } from '../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../elements/forms/InputContainer';
import { IconHeader } from '../../../elements/textElements/iconHeader/IconHeader';

interface IPatientMergeSsoProps {
    onClose: () => Promise<void>;
    patientId: string;
    ssoMergeId: string;
}

interface IPatientMergeSsoReduxProps {
    organizationType: string;
    practitionerProfession: Profession;
}

interface IPatient {
    birthDate: string | null;
    city: string | null;
    country: string | null;
    emailAddress: string | null;
    gender: Gender | null;
    homePhoneNumber: string | null;
    houseLetter: string | null;
    houseNumber: string | null;
    houseNumberSuffix: string | null;
    identifierSocialSecurity: string | null;
    mobileNumber: string | null;
    nameFamily: string;
    nameFamily2: string | null;
    nameGiven: string;
    nameInfix: string | null;
    nameInfix2: string | null;
    nameUsual: string | null;
    state: string | null;
    street: string | null;
    zip: string | null;
}

interface IRadioInputValues {
    birthDate: RadioButtonGroupSelect | null;
    city: RadioButtonGroupSelect | null;
    country: RadioButtonGroupSelect | null;
    emailAddress: RadioButtonGroupSelect | null;
    gender: RadioButtonGroupSelect | null;
    homePhoneNumber: RadioButtonGroupSelect | null;
    houseLetter: RadioButtonGroupSelect | null;
    houseNumber: RadioButtonGroupSelect | null;
    houseNumberSuffixShort: RadioButtonGroupSelect | null;
    identifierSocialSecurity: RadioButtonGroupSelect | null;
    mobileNumber: RadioButtonGroupSelect | null;
    nameFamily: RadioButtonGroupSelect | null;
    nameFamily2: RadioButtonGroupSelect | null;
    nameGiven: RadioButtonGroupSelect | null;
    nameInfix: RadioButtonGroupSelect | null;
    nameInfix2: RadioButtonGroupSelect | null;
    nameUsual: RadioButtonGroupSelect | null;
    state: RadioButtonGroupSelect | null;
    street: RadioButtonGroupSelect | null;
    zip: RadioButtonGroupSelect | null;
}

interface IPatientsMergeSsoState {
    errors: ValidationErrors;
    isSaving: boolean;
    merged?: IPatient;
    needsMerge: boolean;
    patient?: IPatient;
    propertyWhitelist?: string[];
    radioInputValues: IRadioInputValues;
    ssoData?: IPatient;
}

const mapStateToProps = ({ authentication }: IReduxState): IPatientMergeSsoReduxProps => ({
    practitionerProfession: authentication.profession,
    organizationType: authentication.organizationType,
});

const overlapPatientDataWhiteList = [
    'nameGiven',
    'nameFamily',
    'nameInfix',
    'nameUsual',
    'nameFamily2',
    'nameInfix2',
    'birthDate',
    'identifierSocialSecurity',
    'street',
    'houseNumber',
    'houseNumberSuffix',
    'houseLetter',
    'zip',
    'city',
    'country',
    'state',
    'mobileNumber',
    'homePhoneNumber',
    'emailAddress',
    'gender',
];

class InnerPatientMergeSso extends Component<
    IPatientMergeSsoProps & IPatientMergeSsoReduxProps,
    IPatientsMergeSsoState
> {
    private abortableFetch = new AbortableFetch();

    constructor(props: IPatientMergeSsoProps & IPatientMergeSsoReduxProps, context: {}) {
        super(props, context);

        this.state = {
            errors: emptyValidationErrors,
            needsMerge: false,
            isSaving: false,
            radioInputValues: {
                identifierSocialSecurity: null,
                nameGiven: null,
                nameInfix: null,
                nameFamily: null,
                nameUsual: null,
                nameFamily2: null,
                nameInfix2: null,
                birthDate: null,
                gender: null,
                street: null,
                houseNumber: null,
                houseNumberSuffixShort: null,
                houseLetter: null,
                zip: null,
                city: null,
                state: null,
                country: null,
                mobileNumber: null,
                homePhoneNumber: null,
                emailAddress: null,
            },
        };
    }

    public async componentDidMount() {
        await this.loadData();
    }

    public componentWillUnmount() {
        this.abortableFetch.abort();
    }

    public render() {
        return (
            <Loading
                item={this.state.patient}
                loaded={(patient) => (
                    <Form onSubmit={async () => await this.checkAndSave()}>
                        <HorizontalFormGroupRead>
                            <IconHeader
                                icon="neutral-patient"
                                title={getDisplayName(patient)}
                            />
                        </HorizontalFormGroupRead>

                        {this.renderOverlappingInformation()}

                        <FormDivider />

                        {this.state.needsMerge && (
                            <>
                                {this.patientIdentifierSocialSecurityIsEmpty() &&
                                this.state.ssoData &&
                                this.state.ssoData.identifierSocialSecurity &&
                                !this.hasConflictsOtherThanSocialSecurityNumber() ? (
                                    <>
                                        <Alert appearance="warning">
                                            {translateKey('txcs.conflictIdentifierSocialSecurityText')}
                                        </Alert>
                                        <HorizontalFormGroupRead>
                                            <FormLabel>
                                                {translateKey('txcs.conflictIdentifierSocialSecurity')}
                                            </FormLabel>
                                            <InputContainer>
                                                <FormParagraph bold>
                                                    {this.state.ssoData.identifierSocialSecurity}
                                                </FormParagraph>
                                            </InputContainer>
                                        </HorizontalFormGroupRead>
                                    </>
                                ) : (
                                    <>
                                        <Alert appearance="warning">{translateKey('txcs.conflictText')}</Alert>
                                        {this.patientIdentifierSocialSecurityIsEmpty() &&
                                            this.state.ssoData &&
                                            this.state.ssoData.identifierSocialSecurity &&
                                            this.hasConflictsOtherThanSocialSecurityNumber() && (
                                                <HorizontalFormGroupRead>
                                                    <FormLabel>
                                                        {translateKey('txcs.conflictIdentifierSocialSecurity')}
                                                    </FormLabel>
                                                    <InputContainer>
                                                        <FormParagraph bold>
                                                            {this.state.ssoData.identifierSocialSecurity}
                                                        </FormParagraph>
                                                    </InputContainer>
                                                </HorizontalFormGroupRead>
                                            )}
                                        <ListTable>
                                            <TableHead>
                                                <TableRow>
                                                    <TableData>&nbsp;</TableData>
                                                    <TableData bold={true}>{translateKey('txcs.fromHIS')}</TableData>
                                                    <TableData bold={true}>{translateKey('txcs.inKSYOS')}</TableData>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {!this.patientIdentifierSocialSecurityIsEmpty() &&
                                                    this.renderRow(
                                                        'patient.identifierSocialSecurity',
                                                        'identifierSocialSecurity'
                                                    )}
                                                {this.renderRow('common.nameGiven', 'nameGiven')}
                                                {this.renderRow('common.nameInfix', 'nameInfix')}
                                                {this.renderRow('common.nameFamily', 'nameFamily')}
                                                {this.renderRow('patient.nameUsual', 'nameUsual')}
                                                {this.renderRow('patient.nameInfix2', 'nameInfix2')}
                                                {this.renderRow('patient.nameFamily2', 'nameFamily2')}
                                                {this.renderRow(
                                                    'common.birthDate',
                                                    'birthDate',
                                                    patient.birthDate ? patient.birthDate : undefined,
                                                    this.state.ssoData && this.state.ssoData.birthDate
                                                        ? this.state.ssoData.birthDate
                                                        : undefined
                                                )}
                                                {this.renderRow(
                                                    'common.gender',
                                                    'gender',
                                                    patient.gender
                                                        ? translateKey(`genderEnum.${patient.gender}`)
                                                        : undefined,
                                                    this.state.ssoData && this.state.ssoData.gender
                                                        ? translateKey(`genderEnum.${this.state.ssoData.gender}`)
                                                        : undefined
                                                )}
                                                {this.renderRow('common.street', 'street')}
                                                {this.renderRow('common.numberShort', 'houseNumber')}
                                                {this.renderRow(
                                                    'patient.houseNumberSuffixShort',
                                                    'houseNumberSuffixShort'
                                                )}
                                                {this.renderRow('common.houseLetter', 'houseLetter')}
                                                {this.renderRow('common.zipcode', 'zip')}
                                                {this.renderRow('common.city', 'city')}
                                                {this.renderRow('common.state', 'state')}
                                                {this.renderRow('common.country', 'country')}
                                                {this.renderRow('common.mobileNumber', 'mobileNumber')}
                                                {this.renderRow('common.homeNumber', 'homePhoneNumber')}
                                                {this.renderRow('common.emailAddress', 'emailAddress')}
                                            </TableBody>
                                        </ListTable>
                                    </>
                                )}
                            </>
                        )}

                        <ButtonContainer
                            alignRight
                            noMargin
                        >
                            <SaveSubmit
                                disabled={this.state.isSaving || !this.validateRows()}
                                name={'common.saveAndProceed'}
                            />
                        </ButtonContainer>
                    </Form>
                )}
            />
        );
    }

    private hasConflictsOtherThanSocialSecurityNumber() {
        return (
            intersection(
                pull(overlapPatientDataWhiteList, 'identifierSocialSecurity'),
                keys(this.state.ssoData)
            ).filter((key) => !this.valuesAreSame(key)).length > 0
        );
    }

    private renderOverlappingInformation() {
        const overlappingDataKeys = intersection(overlapPatientDataWhiteList, keys(this.state.ssoData)).filter((key) =>
            this.valuesAreSame(key)
        );

        return overlappingDataKeys.map((key) => {
            const currentOverlappingDataValue = this.state.ssoData && this.state.ssoData[key as keyof IPatient];

            return (
                <HorizontalFormGroupRead key={key}>
                    <FormLabel>
                        {doesTranslateKeyExist(`common.${key}`)
                            ? translateKey(`common.${key}`)
                            : translateKey(`patient.${key}`)}
                    </FormLabel>
                    <InputContainer>
                        <FormParagraph bold>
                            {currentOverlappingDataValue}
                            {currentOverlappingDataValue &&
                                currentOverlappingDataValue === this.state.ssoData!.birthDate && (
                                    <>
                                        {' '}
                                        (<DisplayAge value={DateTime.from(currentOverlappingDataValue).toDate()} />)
                                    </>
                                )}
                        </FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            );
        });
    }

    private hasConflict(key: string) {
        return !this.valuesAreSame(key) && this.isNotEmptyHisData(key);
    }

    private valuesAreSame(key: string): boolean {
        let result = false;
        const ssoDataValue = this.getValue(this.state.ssoData, key as keyof IPatient);
        const patientValue = this.getValue(this.state.patient, key as keyof IPatient);
        if (patientValue && ssoDataValue && patientValue.toLocaleLowerCase() === ssoDataValue.toLocaleLowerCase()) {
            result = true;
        }

        return result;
    }

    private isNotEmptyHisData(key: string): boolean {
        let result = false;
        if (this.getValue(this.state.ssoData, key as keyof IPatient)) {
            result = true;
        }

        return result;
    }

    private getValue<T, K extends keyof T>(data: T | undefined, key: K) {
        return data ? data[key] : undefined;
    }

    private renderRow(groupName: string, key: keyof IRadioInputValues, patientValue?: string, ssoValue?: string) {
        if (this.hasConflict(key)) {
            if (!patientValue && this.state.patient) {
                patientValue = this.getValue(this.state.patient, key as keyof IPatient) as string;
            }
            if (!ssoValue && this.state.ssoData) {
                ssoValue = this.getValue(this.state.ssoData, key as keyof IPatient) as string;
            }

            return (
                <TableRow>
                    <TableData>
                        <FormLabel isRequired>{translateKey(groupName)}</FormLabel>
                    </TableData>
                    <TableData>
                        <InputRadio
                            key={key + RadioButtonGroupSelect.leftSelected}
                            label={ssoValue || translateKey('common.unknown')}
                            name={key}
                            onChange={async (selectedValue) => await this.handleSelect(key, selectedValue)}
                            onValue={RadioButtonGroupSelect.leftSelected}
                            value={this.state.radioInputValues[key]}
                        />
                    </TableData>
                    <TableData>
                        <InputRadio
                            italicLabel={!patientValue}
                            key={key + RadioButtonGroupSelect.rightSelected}
                            label={patientValue || translateKey('common.unknown')}
                            name={key}
                            onChange={async (selectedValue) => await this.handleSelect(key, selectedValue)}
                            onValue={RadioButtonGroupSelect.rightSelected}
                            value={this.state.radioInputValues[key]}
                        />
                    </TableData>
                </TableRow>
            );
        }
    }

    private validateRow(key: keyof IRadioInputValues) {
        return !(this.hasConflict(key) && this.state.radioInputValues[key] === null);
    }

    private validateRows() {
        return (
            (this.patientIdentifierSocialSecurityIsEmpty() || this.validateRow('identifierSocialSecurity')) &&
            this.validateRow('nameGiven') &&
            this.validateRow('nameInfix') &&
            this.validateRow('nameFamily') &&
            this.validateRow('nameUsual') &&
            this.validateRow('nameInfix2') &&
            this.validateRow('nameFamily2') &&
            this.validateRow('birthDate') &&
            this.validateRow('gender') &&
            this.validateRow('street') &&
            this.validateRow('houseNumber') &&
            this.validateRow('houseNumberSuffixShort') &&
            this.validateRow('houseLetter') &&
            this.validateRow('zip') &&
            this.validateRow('city') &&
            this.validateRow('state') &&
            this.validateRow('country') &&
            this.validateRow('mobileNumber') &&
            this.validateRow('homePhoneNumber') &&
            this.validateRow('emailAddress')
        );
    }

    private async handleSelect(key: string, selectedValue: string) {
        if (this.state.merged) {
            const patientValue = this.getValue(this.state.patient, key as keyof IPatient);
            const ssoValue = this.getValue(this.state.ssoData, key as keyof IPatient);
            await this.setState(({ merged, radioInputValues }) => ({
                merged: {
                    ...merged,
                    [key]: selectedValue === RadioButtonGroupSelect.rightSelected ? patientValue : ssoValue,
                },
                radioInputValues: { ...radioInputValues, [key]: selectedValue },
            }));
        }
    }

    private async loadData() {
        await this.loadSsoData();
        await this.loadPatient();
        await this.saveMergedPatientInState();
    }

    private async saveMergedPatientInState() {
        let merged = {} as IPatient;

        if (this.state.patient) {
            for (const key of Object.keys(this.state.patient as IPatient)) {
                if ((this.state.propertyWhitelist as string[]).includes(key)) {
                    const value = this.getValue(this.state.patient, key as keyof IPatient);
                    if (value) {
                        merged = { ...merged, [key]: value };
                    }
                }
            }
        }
        await this.setState({ merged });
    }

    private async loadPatient() {
        const url = '/api/patients/' + this.props.patientId + '?includeItemAuthorizations=canUpdateProperties';
        const result = await this.abortableFetch.fetchJson(url, 'GET');

        const patient = result.json;
        const propertyWhitelist = patient.authorization.canUpdateProperties;
        delete patient.authorization;
        await this.setState({
            patient: patient,
            propertyWhitelist: propertyWhitelist,
        });
    }

    private async loadSsoData() {
        const url = `/api/thirdPartySsoData/${this.props.ssoMergeId}`;
        const result = await this.abortableFetch.fetchJson(url, 'GET');
        await this.setState({ ssoData: result.json.data.patient, needsMerge: true });
    }

    private async checkAndSave() {
        if (this.validateRows()) {
            if (this.identifierSocialSecurityIsMerged()) {
                await this.save();
            } else {
                await this.copyIdentifierSocialSecurityFromHis();
                await this.save();
            }
        }
    }

    private async copyIdentifierSocialSecurityFromHis() {
        if (this.state.ssoData) {
            const identifierSocialSecurity = this.state.ssoData.identifierSocialSecurity;
            await this.setState(({ merged }) => ({ merged: { ...merged, identifierSocialSecurity } }));
        } else {
            throw new Error('No SSO data available');
        }
    }

    private identifierSocialSecurityIsMerged() {
        return (
            this.state.merged &&
            this.state.merged.identifierSocialSecurity &&
            this.state.merged.identifierSocialSecurity !== ''
        );
    }

    private patientIdentifierSocialSecurityIsEmpty() {
        return !(
            this.state.patient &&
            this.state.patient.identifierSocialSecurity &&
            this.state.patient.identifierSocialSecurity !== null &&
            this.state.patient.identifierSocialSecurity !== ''
        );
    }

    private async save() {
        const { errors, item } = validate(patientSsoUpdateSchema, this.state.merged);
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }
        const url = `/api/patients/${this.props.patientId}?patientDetailsChangeSource=${PatientDetailsChangeSource.sso}`;
        await this.setState({ isSaving: true });
        await fetchJson(url, 'POST', item);

        this.props.onClose();
    }
}

export const PatientMergeSso = connect(mapStateToProps)(InnerPatientMergeSso);
